import React, {useEffect, useRef, useState} from 'react';
import {Button, Card, Col, Form, Input, Modal, Radio, Row, Space, Upload} from 'antd';
import {SearchOutlined, UploadOutlined} from '@ant-design/icons';
import useHTTP from '@/hooks/useHTTP.mjs';
import runAsync from '@/script/runAsync.mjs';
import Permission from '@/components/Permission.jsx';
import {useOpenModal} from '@/components/Modal/index.mjs';
import SelectPatternModuleDialog from '@/pages/MainPage/dialogs/SelectPatternModuleDialog';
import SelectModuleDialog from '@/pages/MainPage/dialogs/SelectModuleDialog';
import Table from '@/biz/Table.jsx';
import BizNodeTypeTag, {convertToBizNodeTypeName} from "@/components/BizNodeTypeTag/BizNodeTypeTag";
import SelectIsrfDialog from '@/pages/MainPage/dialogs/SelectIsrfDialog.jsx';

/**
 * AI迭代功能方案（软件SPEC单）
 * @param param
 *  modules: [{moduleType, moduleId, moduleName, reqDesc, files}]
 * @param onOk
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const Pane = ({
                  param = {
                      prjPatternId,
                      prjId,
                      sVer,
                      fmIds: [],
                      dmIds: [],
                      bdIds: [],
                      modules: [],
                      reqDesc,
                      files: []
                  }, onOk, ...props
              }) => {
    const http = useHTTP();
    const openModal = useOpenModal();

    // 表单
    const [form] = Form.useForm();

    const [genType, setGenType] = useState('NEW'); // 生单方式

    // 数据
    const [prj, setPrj] = useState({});
    const [prjMapFlag, setPrjMapFlag] = useState(param?.prjPatternId ? false : true);
    const [isrf, setIsrf] = useState({});
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [fileList, setFileList] = useState([]);

    // 加载显示
    const [loading, setLoading] = useState(false);

    const ref = useRef({});

    // 组件初始化
    useEffect(() => {
        (async () => {
            init()
        })()
    }, []);

    const init = async () => {
        try {
            setLoading(true)
            ref.current.fileList = [];
            form.resetFields();

            doQueryPrj({prjId: param.prjId})

            doQueryIsrf()


            if (param.files && param.files.length > 0) {
                setFileList([...param.files])
                ref.current.fileList = [...param.files];
            }
            form.setFieldsValue({genType, reqDesc: param.reqDesc});

            const modules = param.modules || [];
            const fmIds = param.fmIds || [];
            const dmIds = param.dmIds || [];
            if (modules && modules.length > 0) {
                for (var item of modules) {
                    item.pkid = item.moduleId;
                    item.reqTitle = '修改' + (item.moduleName ?? '');
                    item.reqType = 'UPT';
                }
            } else {
                if (fmIds && fmIds.length > 0){
                    const result = await doQueryBdsAndUis(param);
                    const bds = result.dmBds || [];
                    const uis = result.umUis || [];

                    if (bds && bds.length > 0) {
                        for (var item of bds) {
                            item.moduleType = "BD";
                            item.moduleName = item.bdName;
                            item.moduleId = item.pkid;
                            item.moduleCode = item.bdCode;

                            item.pkid = item.pkid;
                            item.reqTitle = '修改' + (item.bdName ?? '');
                            item.reqType = 'UPT';

                            modules.push(item);
                        }
                    }

                    if (uis && uis.length > 0) {
                        for (var item of uis) {
                            item.moduleType = "UI";
                            item.moduleName = item.uiName;
                            item.moduleId = item.pkid;
                            item.moduleCode = item.uiCode;

                            item.pkid = item.pkid;
                            item.reqTitle = '修改' + (item.uiName ?? '');
                            item.reqType = 'UPT';

                            modules.push(item);
                        }
                    }
                }

                if (dmIds && dmIds.length > 0) {
                    const dts = await doQueryDts(param);
                    if (dts && dts.length > 0) {
                        for (var item of dts) {
                            item.moduleType = "DT";
                            item.moduleName = item.dtName;
                            item.moduleId = item.pkid;
                            item.moduleCode = item.dtCode;

                            item.pkid = item.pkid;
                            item.reqTitle = '修改' + (item.dtName ?? '');
                            item.reqType = 'UPT';

                            modules.push(item);
                        }
                    }
                }
            }

            // 程序模块：自动带出其依赖链中所有的程序模块（选择的用户界面不处理）
            const bdIds = param.bdIds || [];
            if (bdIds.length > 0) {
                const bds = await doQueryBdsWithRef(param);
                if (bds && bds.length > 0) {
                    for (const bd of bds) {
                        modules.push({
                            pkid: bd.pkid,
                            moduleType: "BD",
                            moduleName: bd.bdName,
                            moduleId: bd.pkid,
                            moduleCode: bd.bdCode,
                            reqTitle: '修改' + (bd.bdName ?? ''),
                            reqType: 'UPT',
                        });
                    }
                }
            }

            // 按 pkid 去重
            const seen = new Set();
            const dedupedModules = [];
            for (const m of modules) {
                if (!seen.has(m.pkid)) {
                    seen.add(m.pkid);
                    dedupedModules.push(m);
                }
            }

            setRows([...dedupedModules])
        } finally {
            setLoading(false)
        }
    }

    const doQueryPrj = async ({prjId}) => {
        const data = await runAsync(
            async () => {
                return await http.get(`/prjs/${prjId}`);
            }, {
                error: {content: (err) => `查询项目失败: ${err.message}`},
                loading: {show: false},
                success: {show: false},
            });

        setPrj({...data});
    }

    const doQueryBdsAndUis = async ({prjId, sVer, fmIds}) => {
        const result = await http.get(
            '/archfms/listDmBdAndUmUiWithRefByFmIds',
            {prjId, sVer, fmIds}
        )
        return result;
    }

    const doQueryBdsWithRef = async ({prjId, sVer, bdIds}) => {
        const result = await http.get(
            '/archfms/listDmBdWithRefByBdIds',
            {prjId, sVer, bdIds}
        )
        return result;
    }

    const doQueryDts = async ({prjId, sVer, dmIds}) => {
        const result = await http.get(
            '/archdms/listDmDtByDmIds',
            {prjId, sVer, dmIds}
        )
        return result;
    }

    const doQueryIsrf = async () => {
        if (!param.prjPatternId) {
            return
        }
        const data = await runAsync(
            async () => {
                return await http.get(`/isrf/listIsrf`, {
                    prjId: param.prjId,
                    stsCodes: ['DRAFT', 'RLS', 'REVISE'],
                    prjPatternId: param.prjPatternId,
                    freezeFlag: false
                });
            }, {
                error: {show: false},
                loading: {show: false},
                success: {show: false},
            });
        if (data && data.length > 0) {
            setIsrf({...data[0]});

        }
    }

    const genTypeOptions = [
        {
            label: '新建软件SPEC单',
            value: 'NEW',
        },
        {
            label: '加入已有软件SPEC单',
            value: 'OLD',
        },
    ]

    // 生单方式变更
    const onChangeGenType = async (e) => {
        const {value, checked} = e.target
        setGenType(value)

        if (value) {
            // 清空软件SPEC单
            form.setFieldsValue({isrfName: '', isrfId: '', dpSn: ''})
        }

        if (value == 'OLD') {
            if (isrf) {
                form.setFieldsValue({isrfName: isrf.isrfName, isrfId: isrf.pkid, dpSn: isrf.dpSn})
            }
        }
    }


    // 打开选择软件SPEC单对话框
    const openIsrf = async () => {
        const result = await openModal(<SelectIsrfDialog
            params={{
                prjId: param.prjId,
                stsCodes: ['DRAFT', 'RLS', 'REVISE'],
                prjPatternId: param.prjPatternId,
                prjMapFlag: prjMapFlag
            }}/>);

        if (result) {
            const {isrfName, pkid: isrfId, dpSn} = result;
            form.setFieldsValue({isrfName, isrfId, dpSn})
        }
    }

    // 新建软件SPEC单
    const doSave = async () => {
        try {
            setLoading(true);
            const formField = await form.validateFields();

            let result = null;
            if (formField.isrfId) {
                result = {
                    pkid: formField.isrfId,
                    dpSn: formField.dpSn,
                };
            } else {
                const formField = await form.validateFields();
                formField.pkid = formField.isrfId;
                formField.prjMapFlag = prjMapFlag;
                formField.prjMapSVer = param.sVer;

                result = await runAsync(
                    async () => {
                        return await http.post(`/isrf/addAiIsrf`, {
                            ...param,
                            ...formField,
                            modules: [...rows]
                        });
                    }, {
                        error: {content: (err) => `生成失败: ${err.message}`},
                        loading: {show: false},
                        success: {show: true},
                    });

            }

            result = {
                ...result,
                bdIds: rows.filter(v => v.moduleType == 'BD').map(item => item.pkid),
                uiIds: rows.filter(v => v.moduleType == 'UI').map(item => item.pkid),
                dtIds: rows.filter(v => v.moduleType == 'DT').map(item => item.pkid),
                reqDesc: formField.reqDesc,
                files: ref.current.fileList.map(file => ({fileId: file.fileId, name: file.name, size: file.size})),
            };

            onOk(result);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const columns = [
        {
            title: '模件类型',
            dataIndex: 'moduleType',
            width: 100,
            ellipsis: true,
            getValue: record => {
                return convertToBizNodeTypeName(record.moduleType, null, prj.subTypeCode)
            },
            filter: <Table.FilterText/>,
            render: (text, record) => {
                return <BizNodeTypeTag bizNodeType={record.moduleType} subTypeCode={prj.subTypeCode}/>
            }
        },
        {
            title: '模件名称',
            dataIndex: 'moduleName',
            width: 150,
            ellipsis: true,
            filter: <Table.FilterText/>,
        }
    ];

    const cardNoBottomCss = {
        '& .ant-card-body': {
            paddingBottom: '0px',
        },
    }

    const cardSmallTopCss = {
        '& .ant-card-body': {
            paddingTop: '12px',
            paddingBottom: '12px',
        },
    }

    const doAdd = async () => {
        const result = await openModal(<SelectPatternModuleDialog isSingle={false} prjPatternId={param.prjPatternId}
                                                                  filterBizNodeTypes={['BD', 'UI', 'DT']}/>);
        if (result && result.length > 0) {
            result.forEach(item => {
                if (!rows.find(elem => elem.pkid == item.dpId)) {
                    const module = {
                        pkid: item.dpId,
                        moduleType: item.dpBizNodeType,
                        moduleId: item.dpId,
                        moduleName: item.dpName
                    }
                    rows.push(module);
                }

            })
        }
        setRows([...rows]);
    }

    // 选择迭代版模件
    const doAddIterative = async () => {
        let result = await openModal(<SelectModuleDialog isSingle={false} prjId={param.prjId} sVer={param.sVer} filterBizNodeTypes={['BD', 'UI', 'DT']}/>)
        if (result && result.length > 0) {
            result.forEach(item => {
                if (!rows.find(elem => elem.pkid == item.pkid)) {
                    const module = {
                        pkid: item.pkid,
                        moduleType: item.dpBizNodeType,
                        moduleId: item.pkid,
                        moduleName: item.dpName
                    }
                    rows.push(module);
                }

            })
        }
        setRows([...rows]);
    }

    const doRemove = () => {
        const filterArrays = selectedRows.map(({pkid}) => pkid)
        setRows(rows.filter(({pkid}) => !filterArrays.includes(pkid)))
        setSelectedRows([])
    }

    const rowSelection = {
        selectedRowKeys: selectedRows.map(({pkid}) => pkid),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRows(selectedRows);
        },
    }

    const uploadFile = async (data) => {
        try {
            const {fileId} = await runAsync(
                async () => {
                    return await http.upload({'pathName': param.prjId, 'file': data.file});
                }, {
                    error: {content: (err) => `上传失败: ${err.message}`},
                    loading: {show: true},
                    success: {show: false},
                });
            if (fileId) {
                data.file.fileId = fileId;
                ref.current.fileList = ([...ref.current.fileList, data.file]);
                setFileList([...ref.current.fileList])
            }
        } catch (err) {
            console.log(err);
        }
    }

    return (
        <>
            <Modal
                title={'AI迭代功能方案（软件SPEC单）'}
                okText="确定"
                cancelText="取消"
                width={1500}
                bodyStyle={{maxHeight: '70vh', overflowY: 'auto'}}
                {...props}
                footer={[
                    <Space>
                        <Permission permission="ISRF_CRT" projects={[param?.prjId + 1]}>
                            <Button onClick={() => onOk()}>取消</Button>
                            <Button type="primary" onClick={() => doSave()} loading={loading}>确定</Button>
                        </Permission>
                    </Space>
                ]}
            >
                <Form form={form} layout="vertical">


                    <Card title={"软件SPEC单"} css={cardNoBottomCss}>
                        <Row gutter={24}>
                            <Col span={8}>
                                <Form.Item
                                    name={'genType'}
                                    label="  "
                                    onChange={(e) => onChangeGenType(e)}
                                >
                                    <Radio.Group options={genTypeOptions}/>
                                </Form.Item>
                            </Col>
                            {
                                genType == 'OLD' && <Col span={8}>
                                    <Form.Item name="isrfId" hidden><Input allowClear/></Form.Item>
                                    <Form.Item name="dpSn" hidden><Input allowClear/></Form.Item>
                                    <Form.Item
                                        name={'isrfName'}
                                        label="软件SPEC单"
                                        rules={[{required: true, message: '请选择软件SPEC单'}]}
                                    >
                                        <Input placeholder="请选择软件SPEC单"
                                               addonAfter={<SearchOutlined onClick={() => openIsrf()}/>}
                                               readOnly/>
                                    </Form.Item>
                                </Col>
                            }

                            {
                                genType == 'NEW' && <Col span={8}>
                                    <Form.Item name="reqNo" label="业务需求单编号">
                                        <Input allowClear/>
                                    </Form.Item>
                                </Col>
                            }
                            {
                                genType == 'NEW' && <Col span={8}>
                                    <Form.Item name="isrfName" label="软件SPEC单标题"
                                               rules={[{required: true, message: '请输入软件SPEC单标题'}]}>
                                        <Input allowClear/>
                                    </Form.Item>
                                </Col>
                            }
                        </Row>
                    </Card>

                    <Card title={"分析范围"} style={{marginTop: '20px'}} css={cardSmallTopCss}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 12,}}>
                            <Space>
                                {!prjMapFlag &&
                                    <Button type="primary" size={'small'} onClick={() => doAdd()}>新增</Button>}
                                {prjMapFlag && <Button type="primary" size={'small'}
                                                             onClick={() => doAddIterative()}>新增</Button>}
                                <Button type="danger" size={'small'} onClick={() => doRemove()}>删除</Button>
                            </Space>
                        </div>
                        {(loading || rows?.length > 0) && <Table
                            size="middle"
                            columns={columns}
                            dataSource={rows}
                            loading={loading}
                            noRowNumber={true}
                            rowKey={"pkid"}
                            rowSelection={{type: "checkbox", ...rowSelection}}
                        />}
                    </Card>

                    <Card title={"需求描述"} style={{marginTop: '20px'}} css={cardSmallTopCss}>
                        <Form.Item
                            name={'reqDesc'}
                            label=""
                        >
                            <Input.TextArea size="small" rows={5}/>
                        </Form.Item>
                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                            <Upload
                                fileList={fileList}
                                multiple={true}
                                showUploadList={true}
                                customRequest={uploadFile}
                                beforeUpload={(file, files) => {
                                    setFileList([...fileList, ...files]);
                                    return true;
                                }}
                                onRemove={(file) => {
                                    if (file) {
                                        const notDeletes = fileList.filter(f => f.fileId != file.fileId);
                                        setFileList([...notDeletes]);
                                    }
                                }}
                            >
                                <div className="ant-upload-btn bg-grey-light">
                                    <Button type="primary" size={'small'} icon={<UploadOutlined/>}>需求附件上传</Button>
                                </div>
                            </Upload>
                        </div>
                    </Card>
                </Form>
            </Modal>
        </>
    );
}

export default Pane;
